import { useState, type FormEvent } from "react";
import { useCreateMeetingPoint, useMeetingPoints, useSetMeetingPointActive, type MeetingPoints } from "../../api/admin";
import { StatusChip } from "../../components/ui";
import { AdminDrawer, ListSearch, matches } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";

const EMPTY = { name: "", lat: "", lon: "", address: "", region_id: "" };

/** Meeting points: where a crew is collected on the way out. Retired, never
 * deleted, so a plan that used one still describes what was driven. */
export function MeetingPointsPage() {
  const view = useMeetingPoints();
  const setActive = useSetMeetingPointActive();
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const data = view.data;
  const rows = (data?.rows ?? []).filter((p) => matches(query, [p.name, p.address, p.region]));
  return (
    <AdminLayout
      title="Meeting points"
      sub={data?.contract ? `Saved places a day can start from · ${data.contract.name}` : "Saved places a day can start from"}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
          + Add meeting point
        </button>
      }
    >
      <ListSearch value={query} onChange={setQuery} placeholder="Search meeting points" />
      {view.isError ? <p className="notice bad">The meeting points could not be loaded: {view.error.message}</p> : null}
      {setActive.isError ? <p className="notice bad">{setActive.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading meeting points…</div> : null}
      {data && data.rows.length === 0 ? (
        <div className="table-card empty-state">
          <strong>No meeting points yet.</strong> Add the yard or the garage a day usually starts from.
        </div>
      ) : null}
      {data && data.rows.length > 0 ? (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Position</th>
                <th>Region</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.address ?? "—"}</td>
                  <td className="mono">
                    {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                  </td>
                  <td>{p.region ?? "—"}</td>
                  <td>
                    <StatusChip tone={p.active ? "ok" : "quiet"}>{p.active ? "Active" : "Retired"}</StatusChip>
                  </td>
                  <td className="num">
                    <button
                      type="button"
                      className="row-button"
                      disabled={setActive.isPending}
                      onClick={() => setActive.mutate({ id: p.id, active: !p.active })}
                    >
                      {p.active ? "Retire" : "Restore"}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No meeting point matches “{query}”.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
      {adding ? (
        <AdminDrawer title="Add a meeting point" onClose={() => setAdding(false)}>
          <AddMeetingPoint regions={data?.regions ?? []} onDone={() => setAdding(false)} />
        </AdminDrawer>
      ) : null}
    </AdminLayout>
  );
}

function AddMeetingPoint({ regions, onDone }: { regions: MeetingPoints["regions"]; onDone: () => void }) {
  const create = useCreateMeetingPoint();
  const [form, setForm] = useState(EMPTY);
  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        name: form.name.trim(),
        lat: form.lat.trim() === "" ? null : Number(form.lat),
        lon: form.lon.trim() === "" ? null : Number(form.lon),
        address: form.address.trim() || null,
        region_id: form.region_id === "" ? null : Number(form.region_id),
      },
      { onSuccess: onDone },
    );
  }
  return (
    <form className="admin-form" onSubmit={submit}>
      <label className="field">
        Name
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </label>
      <div className="admin-form-grid">
        <label className="field">
          Latitude
          <input inputMode="decimal" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} required />
        </label>
        <label className="field">
          Longitude
          <input inputMode="decimal" value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })} required />
        </label>
      </div>
      <label className="field">
        Address
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </label>
      <label className="field">
        Region
        <select value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })}>
          <option value="">—</option>
          {regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </label>
      {create.isError ? <p className="notice bad">{create.error.message}</p> : null}
      <div className="admin-form-actions">
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Add
        </button>
      </div>
    </form>
  );
}
